<template>
  <div
    :class="{
      '-mt-16': noTopMargin
    }"
  >
    <div class="relative flex items-center">
      <ion-icon
        v-if="isSearchInput"
        class="absolute left-16 w-20 h-20 z-10"
        aria-hidden="true"
        :icon="search"
      />
      <div v-if="showOptional" class="absolute right-16 top-[18px] z-[1000] text-tiny uppercase text-dark">
        VALFRI
      </div>
      <ion-textarea
        v-if="isTextArea"
        mode="md"
        class="cursor-pointer"
        :class="{
          'search-input': isSearchInput,
          'ion-invalid': invalid,
          'fixed-placeholder': darkBackground,
        }"
        color="primary"
        :value="value"
        :label="placeholder"
        label-placement="floating"
        fill="outline"
        :rows="4"
        placeholder="Skriv..."
        :type="type ? type : 'text'"
        :debounce="useDebounce && 500"
        @keydown.enter="onEnter"
        @ion-input="$emit('onInput', $event.target.value)"
      />
      <ion-input
        v-else
        mode="md"
        class="cursor-pointer"
        :class="{
          'search-input': isSearchInput,
          'ion-invalid': invalid,
          'fixed-placeholder': darkBackground,
          'regular': !isSearchInput && !invalid && !darkBackground,
          '!hover:border-border-primary': disabled,
        }"
        color="primary"
        :value="value"
        :label="placeholder"
        :disabled="disabled"
        label-placement="floating"
        fill="outline"
        placeholder="Skriv..."
        :type="type ? type : 'text'"
        :inputmode="numeric ? 'decimal' : undefined"
        :debounce="useDebounce && 500"
        @keydown.enter="onEnter"
        @ion-input="onIonInput"
      />
      <div v-if="subText" class="absolute right-16 mt-[14px] text-tiny uppercase">
        {{ subText }}
      </div>
    </div>
    <div v-if="invalid && errorText" class="mt-12 text-start headline-small text-error">
      {{ errorText }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { IonInput, IonIcon, IonTextarea } from '@ionic/vue';
import { search } from 'ionicons/icons';
import { sanitizeNumericInput } from '@/utils/validation';

const props = withDefaults(defineProps<{
  placeholder: string;
  darkBackground?: boolean;
  subText?: string;
  type?: 'date' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | 'time';
  isSearchInput?: boolean;
  value: string | null;
  invalid?: boolean;
  errorText?: string;
  showOptional?: boolean;
  useDebounce?: boolean;
  enterEnabled?: boolean;
  noTopMargin?: boolean; // removes topmargin with negative margin
  isTextArea?: boolean;
  disabled?: boolean;
  numeric?: boolean; // digits and one decimal comma, shows decimal keypad on mobile
}>(), {
  value: '',
  showOptional: false,
  noTopMargin: false,
  enterEnabled: false,
  isTextArea: false,
  disabled: false,
  numeric: false,
});

const emits = defineEmits<{
  (e: 'onInput', payload: any): void
  (e: 'onEnter'): void
}>();

const onEnter = (e) => {
  if (props.enterEnabled) {
    emits('onEnter');
  } else {
    e.preventDefault();
  }
};

const onIonInput = (e) => {
  const input = e.target;
  let value = input.value;

  if (props.numeric && typeof value === 'string') {
    const sanitized = sanitizeNumericInput(value);
    // Write back to the element, since the bound value may not change (e.g. "12" -> "12a" -> "12")
    if (sanitized !== value) {
      input.value = sanitized;
    }
    value = sanitized;
  }

  emits('onInput', value);
};

</script>
<style>
.input-fill-outline.sc-ion-input-md-h {
  --border-radius: 6px;
  --border-color: var(--ion-border-color);
}

.search-input .native-wrapper {
  padding-left: 38px !important;
}

.search-input .label-text-wrapper {
  margin-left: 38px !important;
}

.native-wrapper {
  border: 0;
}

.cloned-input {
  height: 100% !important;
  top: -6px !important;
}

.search-input .cloned-input {
  margin-left: 38px !important;
}

.search-input.has-focus.input-label-placement-floating.sc-ion-input-md-h
.label-text-wrapper.sc-ion-input-md,
.search-input.has-value.input-label-placement-floating.sc-ion-input-md-h
.label-text-wrapper.sc-ion-input-md {
  transform: translateY(-50%) translateX(calc(0% - 38px)) scale(0.75) !important;
  color: var(--ion-custom-text-color, var(--ion-color-primary));
}

.fixed-placeholder.has-focus.input-label-placement-floating.sc-ion-input-md-h
.label-text-wrapper.sc-ion-input-md,
.fixed-placeholder.has-value.input-label-placement-floating.sc-ion-input-md-h
.label-text-wrapper.sc-ion-input-md {
  background: white;
  padding: 0px 6px;
  color: var(--ion-custom-text-color, var(--ion-color-primary));
}

.regular.has-focus.input-label-placement-floating.sc-ion-input-md-h
.label-text-wrapper.sc-ion-input-md,
.regular.has-value.input-label-placement-floating.sc-ion-input-md-h
.label-text-wrapper.sc-ion-input-md {
  color: var(--ion-custom-text-color, var(--ion-color-primary));
}


.fixed-placeholder.has-focus.input-label-placement-floating.sc-ion-input-md-h,
.fixed-placeholder.has-value.input-label-placement-floating.sc-ion-input-md-h {
  --border-width: 0 !important;
}

.ion-invalid.input-label-placement-floating.sc-ion-input-md-h {
  --border-color: var(--ion-color-danger);
  --border-width: 2px;
}

.ion-invalid.input-label-placement-floating.sc-ion-input-md-h
.label-text-wrapper.sc-ion-input-md{
  color: var(--ion-color-danger);
}

.input-fill-outline.input-disabled.sc-ion-input-md-h:hover {
  --border-color: var(--ion-border-color);
}
</style>
